import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  AdminCotacoesDTO,
  CotacaoHojeDTO,
  HistoricoDaPracaDTO,
  HistoricoDTO,
  MinhasPorSemanaDTO,
  MinhasPracasDTO,
  MinhasVsPracaDTO,
  PagouDTO,
  PeriodoHistorico,
  PracaDTO,
} from "@shared/mercado-cotacoes";
import type { CotacaoPainelDTO } from "@shared/mercado-inicio";
import type { ChaveProduto } from "@shared/mercado-produtos";
import { useAuth } from "@/lib/auth-context";

/**
 * As Cotações da praça, do lado do cliente.
 *
 * ⚠️ Nenhum hook aqui calcula dinheiro. Preço, média ponderada, delta e variação
 * do período chegam prontos do servidor — a tela formata (`fmtMoedaBR`) e nada
 * mais. Uma média refeita aqui divergiria da do servidor na primeira venda com kg
 * fracionado, e as duas apareceriam na mesma tela.
 *
 * ⚠️ `fetchJson` em toda `queryFn` (checa `r.ok`): sem isso um 500 vira "dado" e a
 * tela quebra branca no primeiro `.map`.
 */

export const CHAVE_COTACOES = "/api/mercado/cotacoes";
export const CHAVE_COTACOES_ADMIN = "/api/mercado/admin/cotacoes";
export const CHAVE_PRACAS_ADMIN = "/api/mercado/admin/pracas";

/** As praças ATIVAS — rota pública, sem sessão. */
export function usePracas() {
  return useQuery<PracaDTO[]>({
    queryKey: ["/api/mercado/pracas"],
    queryFn: () => fetchJson<PracaDTO[]>("/api/mercado/pracas"),
  });
}

/**
 * O quadro do dia de uma praça.
 *
 * `ate` existe para a página pública do romaneio, que mostra a praça da DATA DA
 * VENDA — nunca a de hoje. Sem cotação a resposta é 200 com `linhas: []`; quem
 * desenha o card honesto é `QuadroPraca`.
 */
export function useCotacaoHoje(praca: string | undefined, ate?: string) {
  const params = new URLSearchParams();
  if (praca) params.set("praca", praca);
  if (ate) params.set("ate", ate);
  const qs = params.toString();
  return useQuery<CotacaoHojeDTO>({
    queryKey: [CHAVE_COTACOES, "hoje", praca ?? "", ate ?? ""],
    queryFn: () => fetchJson<CotacaoHojeDTO>(`${CHAVE_COTACOES}/hoje${qs ? `?${qs}` : ""}`),
    enabled: !!praca || !!ate,
  });
}

export function useHistoricoCotacoes(
  praca: string | undefined,
  produto: ChaveProduto,
  periodo: PeriodoHistorico,
) {
  const params = new URLSearchParams({ variedade: produto.variedade, periodo });
  if (praca) params.set("praca", praca);
  if (produto.qualidade) params.set("qualidade", produto.qualidade);
  return useQuery<HistoricoDTO>({
    queryKey: [CHAVE_COTACOES, "historico", praca ?? "", produto.variedade, produto.qualidade ?? "", periodo],
    queryFn: () => fetchJson<HistoricoDTO>(`${CHAVE_COTACOES}/historico?${params.toString()}`),
    enabled: !!praca,
  });
}

/**
 * A praça inteira, semana a semana — a matriz do comprador. Pública, sem sessão.
 *
 * "Ver mais 8 semanas" pede `semanas` maior (16, 24…) em vez de uma página
 * nova: a variação da última linha já vem certa do servidor, e o "você pagou"
 * pede o MESMO número de semanas e cai nas mesmas linhas. `keepPreviousData`
 * segura a tabela na tela enquanto a maior chega.
 */
export function useHistoricoDaPraca(praca: string | undefined, semanas: number) {
  const params = new URLSearchParams({ semanas: String(semanas) });
  if (praca) params.set("praca", praca);
  return useQuery<HistoricoDaPracaDTO>({
    queryKey: [CHAVE_COTACOES, "historico", "todos", praca ?? "", semanas],
    queryFn: () => fetchJson<HistoricoDaPracaDTO>(`${CHAVE_COTACOES}/historico?${params.toString()}`),
    enabled: !!praca,
    placeholderData: keepPreviousData,
  });
}

// ─── Comprador (QUAL praça é a dele — o preço em si é público) ───────────────

export const CHAVE_MINHAS_PRACAS = "/api/mercado/comprador/pracas";

/**
 * As praças deste comprador.
 *
 * ⚠️ NÃO é `usePracas()`. Aquela lista TODAS as praças ativas do cadastro, para
 * o seletor de escolha; esta lista as DELE, que é o filtro que a tela abre. Na
 * praça única, que é o caso do corretor típico, a segunda tem um item e a
 * primeira teria dez.
 */
export function useMinhasPracas(ativo: boolean) {
  return useQuery<MinhasPracasDTO>({
    queryKey: [CHAVE_MINHAS_PRACAS],
    queryFn: () => fetchJson<MinhasPracasDTO>(CHAVE_MINHAS_PRACAS),
    enabled: ativo,
  });
}

/** Gravar "minhas praças" — a escolha do comprador, contra o cadastro. */
export function useDefinirMinhasPracas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pracas: string[]) =>
      apiRequest("PATCH", CHAVE_MINHAS_PRACAS, { pracas }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_MINHAS_PRACAS] });
      // A tela Praça (desligada hoje) lê a mesma escolha — invalidar as duas
      // evita que ela volte mostrando a praça antiga quando religar.
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/praca"] });
    },
  });
}

// ─── Produtor (as MINHAS vendas contra a praça) ──────────────────────────────

export const CHAVE_PRODUTOR_COTACOES = "/api/mercado/produtor";

/** Também é quem diz qual é a praça da org — o quadro da home sai daqui. */
export function useMinhasVsPraca(periodo: string, ativo: boolean) {
  return useQuery<MinhasVsPracaDTO>({
    queryKey: [CHAVE_PRODUTOR_COTACOES, "minhas-vs-praca", periodo],
    queryFn: () => fetchJson<MinhasVsPracaDTO>(`${CHAVE_PRODUTOR_COTACOES}/minhas-vs-praca?periodo=${periodo}`),
    enabled: ativo,
  });
}

/**
 * A praça da organização — o filtro que toda tela de Cotações abre selecionado.
 *
 * Quem resolve é o SERVIDOR, em `minhas-vs-praca`: a praça declarada da org,
 * senão a primeira ativa do cadastro. `undefined` aqui significa "não há praça
 * ativa nenhuma" (ou a query não rodou) — e aí a tela mostra o estado vazio.
 *
 * ⚠️ Sem constante de fallback. Isto caía em `PRACA_PADRAO` (`'irece'`), que
 * sobreviveu à renomeação da praça para Mirorós e teria sobrevivido à 060, que
 * apagou o slug: a tela pediria uma praça que não existe mais e receberia 404.
 */
export function usePracaDaOrg(): { praca: string | undefined; nome: string | undefined; carregando: boolean } {
  const { user, modulesEnabled } = useAuth();
  const ativo = user?.orgTipo !== "COMPRADOR" && modulesEnabled.indexOf("mercado") >= 0;
  const { data, isLoading } = useMinhasVsPraca("mes", ativo);
  if (isLoading) return { praca: undefined, nome: undefined, carregando: true };
  return { praca: data?.praca ?? undefined, nome: data?.pracaNome ?? undefined, carregando: false };
}

export function useMinhasPorSemana(produto: ChaveProduto, periodo: PeriodoHistorico, ativo: boolean) {
  const params = new URLSearchParams({ variedade: produto.variedade, periodo });
  if (produto.qualidade) params.set("qualidade", produto.qualidade);
  return useQuery<MinhasPorSemanaDTO>({
    queryKey: [CHAVE_PRODUTOR_COTACOES, "minhas-por-semana", produto.variedade, produto.qualidade ?? "", periodo],
    queryFn: () => fetchJson<MinhasPorSemanaDTO>(`${CHAVE_PRODUTOR_COTACOES}/minhas-por-semana?${params.toString()}`),
    enabled: ativo,
  });
}

/** Trocar a praça no filtro GRAVA a escolha — o quadro e o comparativo seguem. */
export function useDefinirPraca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (praca: string) =>
      apiRequest("PATCH", `${CHAVE_PRODUTOR_COTACOES}/praca`, { praca }).then(r => r.json()) as Promise<PracaDTO>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR_COTACOES] });
      qc.invalidateQueries({ queryKey: [CHAVE_COTACOES] });
    },
  });
}

// ─── Admin de plataforma (publicar) ──────────────────────────────────────────

export function useAdminCotacoes(praca: string, data: string, ativo: boolean) {
  const params = new URLSearchParams({ praca, data });
  return useQuery<AdminCotacoesDTO>({
    queryKey: [CHAVE_COTACOES_ADMIN, praca, data],
    queryFn: () => fetchJson<AdminCotacoesDTO>(`${CHAVE_COTACOES_ADMIN}?${params.toString()}`),
    enabled: ativo && !!praca && !!data,
  });
}

export interface PublicarCotacoes {
  praca: string;
  data: string;
  /**
   * PRODUTOR | VENDA — o que a placa daquele dia dizia. Obrigatório: o servidor
   * devolve 400 sem ele, de propósito (059). Nada aqui converte um no outro.
   */
  referencial: string;
  /** Até quando o preço é fresco. Ausente = `data + 6`, decidido no servidor. */
  validaAte?: string;
  linhas: Array<{ variedade: string; qualidade: string | null; precoKg: number }>;
  fonte?: string | null;
  observacao?: string | null;
}

/** Publicar de novo o mesmo dia ATUALIZA (upsert no servidor), nunca duplica. */
export function usePublicarCotacoes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: PublicarCotacoes) =>
      apiRequest("PUT", CHAVE_COTACOES_ADMIN, dados).then(r => r.json()),
    onSuccess: () => invalidarCotacoes(qc),
  });
}

export function useExcluirCotacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `${CHAVE_COTACOES_ADMIN}/${id}`).then(r => r.json()),
    onSuccess: () => invalidarCotacoes(qc),
  });
}

function invalidarCotacoes(qc: ReturnType<typeof useQueryClient>) {
  // Publicar muda o quadro do produtor, o comparativo e o histórico do admin —
  // os três leem a mesma tabela e precisam recarregar juntos.
  qc.invalidateQueries({ queryKey: [CHAVE_COTACOES_ADMIN] });
  qc.invalidateQueries({ queryKey: [CHAVE_COTACOES] });
  qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR_COTACOES] });
}

// ─── Admin de plataforma (praças) ────────────────────────────────────────────

export interface PracaAdminDTO extends PracaDTO {
  ativa: boolean;
}

export function usePracasAdmin(ativo: boolean) {
  return useQuery<PracaAdminDTO[]>({
    queryKey: [CHAVE_PRACAS_ADMIN],
    queryFn: () => fetchJson<PracaAdminDTO[]>(CHAVE_PRACAS_ADMIN),
    enabled: ativo,
  });
}

export function useCriarPraca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { nome: string; uf: string; ativa?: boolean }) =>
      apiRequest("POST", CHAVE_PRACAS_ADMIN, dados).then(r => r.json()) as Promise<PracaAdminDTO>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRACAS_ADMIN] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/pracas"] });
    },
  });
}

export function useAtualizarPraca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { slug: string; nome?: string; uf?: string; ativa?: boolean }) =>
      apiRequest("PATCH", `${CHAVE_PRACAS_ADMIN}/${dados.slug}`, {
        nome: dados.nome,
        uf: dados.uf,
        ativa: dados.ativa,
      }).then(r => r.json()) as Promise<PracaAdminDTO>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRACAS_ADMIN] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/pracas"] });
    },
  });
}

/** O card "Praça · hoje" com o "seu preço médio" — o lado do comprador. */
export function usePainelCotacoesComprador(praca: string | undefined) {
  return useQuery<CotacaoPainelDTO>({
    queryKey: ["/api/mercado/comprador/cotacoes/painel", praca ?? ""],
    queryFn: () => fetchJson<CotacaoPainelDTO>(`/api/mercado/comprador/cotacoes/painel?praca=${encodeURIComponent(praca ?? "")}`),
    enabled: !!praca,
  });
}

/** "Você pagou" nas MESMAS semanas de `useHistoricoDaPraca(praca, semanas)`. */
export function usePagouComprador(praca: string | undefined, semanas: number, ativo: boolean) {
  const params = new URLSearchParams({ semanas: String(semanas) });
  if (praca) params.set("praca", praca);
  return useQuery<PagouDTO>({
    queryKey: ["/api/mercado/comprador/cotacoes/pagou", praca ?? "", semanas],
    queryFn: () => fetchJson<PagouDTO>(`/api/mercado/comprador/cotacoes/pagou?${params.toString()}`),
    enabled: !!praca && ativo,
    placeholderData: keepPreviousData,
  });
}
